import { useAtomValue } from 'jotai';
import {
    Card,
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay1,
    LabelDisplay2,
    LabelTelescope,
    SecondaryButton,
    Spacer,
    Textarea,
} from '../../components';
import { exercisesAtom } from '../../data';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { IllustrationCrossroads } from '../../icons';
import { getExercise } from '../../exerciseAPI';
import { useEffect, useState } from 'react';

const startActionTextKey = 'module3Exercise5StartActionText';
const stopActionTextKey = 'module3Exercise5StopActionText';
const continueActionTextKey = 'module3Exercise5ContinueActionText';

const module2Exercise8Id = 510;
const module2Exercise8StartActionTextKey = 'module2Exercise8StartActionText';
const module2Exercise8StopActionTextKey = 'module2Exercise8StopActionText';
const module2Exercise8ContinueActionTextKey =
    'module2Exercise8ContinueActionText';

const actionItems = [
    {
        label: 'Hvad vil du starte med at gøre?',
        key: startActionTextKey,
        module2Key: module2Exercise8StartActionTextKey,
    },
    {
        label: 'Hvad vil du stoppe med at gøre?',
        key: stopActionTextKey,
        module2Key: module2Exercise8StopActionTextKey,
    },
    {
        label: 'Hvad vil du fortsætte med at gøre?',
        key: continueActionTextKey,
        module2Key: module2Exercise8ContinueActionTextKey,
    },
];
export const exerciseData35: ExerciseData = {
    ExerciseView: Module3Exercise5,
    dataKeys: actionItems.map((item) => item.key),
    questions: actionItems.map(({ label, key, module2Key }) => ({
        dataKeys: [key],
        Answers: ({ exercise, className }) => {
            const exercises = useAtomValue(exercisesAtom);
            const module2Exercise8 = exercises.find(
                (x) => x.Id === module2Exercise8Id,
            );

            return (
                <div className={className}>
                    <LabelDisplay1>{label}</LabelDisplay1>
                    <LabelBodyHeadline className="mt-20 text-dark-red">
                        Dit svar modul 2
                    </LabelBodyHeadline>
                    <LabelBody>
                        {module2Exercise8?.Data?.[module2Key]}
                    </LabelBody>
                    <LabelBodyHeadline className="mt-20 text-dark-red">
                        Dit svar i modul 3
                    </LabelBodyHeadline>
                    <LabelBody>{exercise.Data?.[key]}</LabelBody>
                </div>
            );
        },
    })),
};

function Module3Exercise5({ exercise, onContinue }: ExerciseViewProps) {
    const [module2Exercise8Data, setModule2Exercise8Data] =
        useState<ExerciseDto>();
    useEffect(() => {
        getExercise(module2Exercise8Id).then((data) => {
            setModule2Exercise8Data(data);
        });
    }, []);
    const [startActionText, setStartActionText] = useSyncedState<string>(
        exercise.Data[startActionTextKey] ?? '',
    );
    const [stopActionText, setStopActionText] = useSyncedState<string>(
        exercise.Data[stopActionTextKey] ?? '',
    );
    const [continueActionText, setContinueActionText] = useSyncedState<string>(
        exercise.Data[continueActionTextKey] ?? '',
    );

    return (
        <Exercise title="Start, stop og fortsæt">
            <LabelDisplay2 className="mb-10 mt-20">
                Genbesøg de øvelser du kan lide
            </LabelDisplay2>
            <LabelBody>
                I modul 2 lavede du en start/stop/fortsæt. Du inviteres nu til
                at kigge på den igen- og hvis du har lyst, så lave en ny.
            </LabelBody>
            <IllustrationCrossroads className="mx-auto my-30" />
            <Card className="mb-30">
                <LabelDisplay1 className="mb-30">
                    Små ændringer i hverdagen
                </LabelDisplay1>
                <LabelBodyHeadline className="text-dark-red">
                    Hvad vil du starte med at gøre?
                </LabelBodyHeadline>
                <LabelTelescope class="my-10">
                    {
                        module2Exercise8Data?.Data?.[
                            module2Exercise8StartActionTextKey
                        ]
                    }
                </LabelTelescope>
                <Textarea
                    className="mb-30"
                    text={startActionText}
                    onChangeText={setStartActionText}
                    placeholder="F.eks. Jeg vil bruge en vejrtrækningsøvelse, når jeg føler mig presset."
                />
                <LabelBodyHeadline className="text-dark-red">
                    Hvad vil du stoppe med at gøre?
                </LabelBodyHeadline>
                <LabelTelescope class="my-10">
                    {
                        module2Exercise8Data?.Data?.[
                            module2Exercise8StopActionTextKey
                        ]
                    }
                </LabelTelescope>
                <Textarea
                    className="mb-30"
                    text={stopActionText}
                    onChangeText={setStopActionText}
                    placeholder="F.eks. Jeg vil stoppe med at bebrejde mig selv, når jeg bliver ked af det."
                />
                <LabelBodyHeadline className="text-dark-red">
                    Hvad vil du fortsætte med at gøre?
                </LabelBodyHeadline>
                <LabelTelescope class="my-10">
                    {
                        module2Exercise8Data?.Data?.[
                            module2Exercise8ContinueActionTextKey
                        ]
                    }
                </LabelTelescope>
                <Textarea
                    className="mb-10"
                    text={continueActionText}
                    onChangeText={setContinueActionText}
                    placeholder="F.eks. jeg vil fortsætte med at give mig selv små pauser i løbet af dagen."
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                onClick={() => {
                    onContinue({
                        status: ExerciseStatus.Completed,
                        goToNext: '3.5.completed',
                        data: {
                            [startActionTextKey]:
                                normalizeInput(startActionText),
                            [stopActionTextKey]: normalizeInput(stopActionText),
                            [continueActionTextKey]:
                                normalizeInput(continueActionText),
                        },
                    });
                }}
            >
                Gem i logbogen
            </SecondaryButton>
        </Exercise>
    );
}
